import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BellRing, X, Loader2, CheckCircle2 } from "lucide-react";
import { isStandalone, pushSupported, getPushSubscription, enablePush, notifPermission } from "@/lib/pwa";

const KEY = "et-notif-dismissed";
const SANS = { fontFamily: "'Manrope', system-ui, sans-serif" };

/** Tras instalar la PWA (modo standalone), ofrece activar notificaciones con un solo toque.
 *  Solo se muestra si la app está instalada, soporta push, no hay permiso/suscripción aún y no
 *  se ha descartado antes. Texto de cara al cliente: SIEMPRE en inglés. */
export default function PushNudge({ token }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    if (!isStandalone() || !pushSupported()) return undefined;
    if (notifPermission() === "granted") return undefined;
    let dismissed = false;
    try { dismissed = localStorage.getItem(KEY) === "1"; } catch (_) {}
    if (dismissed) return undefined;
    let cancelled = false;
    let timer;
    getPushSubscription().then((sub) => {
      if (cancelled || sub) return;
      timer = setTimeout(() => { if (!cancelled) setShow(true); }, 1800);
    });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [token]);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(KEY, "1"); } catch (_) {}
  };

  const turnOn = async () => {
    if (busy) return;
    setBusy(true);
    const res = await enablePush(token);
    setBusy(false);
    if (res.ok) {
      setDone(true);
      setTimeout(() => setShow(false), 2400);
    } else {
      // Si lo bloquean o falla, no insistimos más.
      try { localStorage.setItem(KEY, "1"); } catch (_) {}
      setShow(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[2147482000] flex justify-center p-3 print:hidden" data-testid="push-nudge" style={SANS}>
      <div className="flex w-full max-w-md items-center gap-3 rounded-lg border border-[#E4E4E7] bg-white p-4 shadow-2xl">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-[#121B28]">
          {done ? <CheckCircle2 className="h-5 w-5 text-white" /> : <BellRing className="h-5 w-5 text-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-[#1A1A1A]">
            {done ? "You're all set" : "Get a heads-up when we reply"}
          </p>
          <p className="mt-0.5 text-xs font-light leading-snug text-[#52525B]">
            {done ? "We'll notify you right here." : "Turn on notifications — no need to keep checking."}
          </p>
        </div>
        {!done && (
          <button onClick={turnOn} disabled={busy} data-testid="push-nudge-cta"
            className="flex-none whitespace-nowrap rounded-full bg-[#121B28] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Turn on"}
          </button>
        )}
        <button onClick={dismiss} data-testid="push-nudge-dismiss" className="flex-none rounded-full p-1.5 text-[#A1A1AA] transition-colors hover:text-[#1A1A1A]" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>,
    document.body
  );
}
